import { useState } from "react";
import { Ban, CheckCircle2, FolderInput, Loader2, TrendingDown, TrendingUp, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, CATEGORIES } from "@/lib/api";

const seg = (on) => `px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${on ? "bg-forest text-paper" : "text-ink/70 hover:bg-sage-200"}`;

const Toggle = ({ options, value, onChange, prefix }) => (
  <div className="flex bg-paper rounded-lg p-0.5 border border-sage-200">
    {options.map(([v, l]) => (
      <button key={v} type="button" className={seg(value === v)} onClick={() => onChange(v)} data-testid={`${prefix}-${v}`}>{l}</button>
    ))}
  </div>
);

export const BulkBar = ({ ids, onDone, onClear }) => {
  const [dir, setDir] = useState("up");
  const [mode, setMode] = useState("percent");
  const [value, setValue] = useState("");
  const [round, setRound] = useState(true);
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (question, fn) => {
    if (!window.confirm(question)) return;
    setBusy(true);
    try {
      await fn();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const n = ids.length;
  const word = dir === "up" ? "Naikkan" : "Turunkan";

  const changePrice = () => {
    const v = Number(value);
    if (!v || v <= 0) return toast.error("Isi besar perubahan harga lebih dari 0");
    if (dir === "down" && mode === "percent" && v >= 100) return toast.error("Potongan persen harus di bawah 100%");
    const label = mode === "percent" ? `${v}%` : `Rp${v.toLocaleString("id-ID")}`;
    run(`${word} harga ${n} menu sebesar ${label}?`, async () => {
      const { data } = await api.post("/admin/menu/bulk-price", { ids, mode, direction: dir, value: v, round_to: round ? 100 : 1 });
      toast.success(`Harga ${data.updated} menu di${dir === "up" ? "naikkan" : "turunkan"} ${label}`);
      setValue("");
    });
  };

  const update = (body, question, done) =>
    run(question, async () => {
      await api.post("/admin/menu/bulk-update", { ids, ...body });
      toast.success(done);
    });

  const moveCat = () => {
    if (!cat) return toast.error("Pilih kategori tujuan dulu");
    const label = CATEGORIES.find((c) => c.id === cat)?.label;
    update({ category: cat }, `Pindahkan ${n} menu ke kategori ${label}?`, `${n} menu dipindah ke ${label}`);
  };

  const btn = "px-3 py-2 rounded-lg border bg-paper text-sm font-semibold flex items-center gap-1.5 transition-colors";
  return (
    <div className="sticky top-2 z-20 mt-4 card-paper bg-sage-100 border-forest/30 p-3 shadow-md space-y-3" data-testid="menu-bulk-bar">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-semibold text-sm px-1" data-testid="menu-bulk-count">{n} dipilih</span>
        <Toggle options={[["up", "Naik"], ["down", "Turun"]]} value={dir} onChange={setDir} prefix="bulk-direction" />
        <Toggle options={[["percent", "%"], ["amount", "Rp"]]} value={mode} onChange={setMode} prefix="bulk-mode" />
        <input type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)} placeholder={mode === "percent" ? "mis. 10" : "mis. 2000"}
          className="w-28 px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm" data-testid="bulk-price-value-input" />
        <label className="flex items-center gap-1.5 text-xs text-muted2">
          <input type="checkbox" checked={round} onChange={(e) => setRound(e.target.checked)} className="accent-[#155EAD]" data-testid="bulk-round-checkbox" /> Bulatkan ke Rp100
        </label>
        <button onClick={changePrice} disabled={busy} className="btn-forest px-4 py-2 text-sm flex items-center gap-1.5" data-testid="bulk-change-price-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : dir === "up" ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />} {word} harga
        </button>
        <button onClick={onClear} className="ml-auto p-2 rounded-lg hover:bg-sage-200" aria-label="Batal pilih" data-testid="bulk-clear-button"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-sage-200">
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-40 bg-paper" data-testid="bulk-category-select"><SelectValue placeholder="Kategori tujuan" /></SelectTrigger>
          <SelectContent>{CATEGORIES.slice(1).map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={moveCat} disabled={busy} className={`${btn} border-forest/40 text-forest hover:bg-sage-50`} data-testid="bulk-move-category-button"><FolderInput className="w-4 h-4" /> Pindahkan</button>
        <span className="hidden sm:block w-px h-6 bg-sage-200" />
        <button onClick={() => update({ available: false }, `Tandai ${n} menu habis?`, `${n} menu ditandai habis`)} disabled={busy}
          className={`${btn} border-amber-300 text-amber-800 hover:bg-amber-50`} data-testid="bulk-mark-unavailable-button"><Ban className="w-4 h-4" /> Tandai habis</button>
        <button onClick={() => update({ available: true }, `Tandai ${n} menu tersedia?`, `${n} menu ditandai tersedia`)} disabled={busy}
          className={`${btn} border-sage-200 text-ink hover:bg-sage-50`} data-testid="bulk-mark-available-button"><CheckCircle2 className="w-4 h-4" /> Tandai tersedia</button>
        <button onClick={() => run(`Hapus ${n} menu terpilih? Tindakan ini tidak bisa dibatalkan.`, async () => {
          const { data } = await api.post("/admin/menu/bulk-delete", { ids });
          toast.success(`${data.deleted} menu dihapus`);
        })} disabled={busy} className={`${btn} border-red-200 text-red-700 hover:bg-red-50 sm:ml-auto`} data-testid="bulk-delete-button"><Trash2 className="w-4 h-4" /> Hapus</button>
      </div>
    </div>
  );
};
